import React, { useEffect, useMemo, useState } from "react";

import {
  collectStockLocations,
  fill,
  formatDateTime,
  getLocationStock,
  getLocationsWithStock,
  getMovementLabelKey,
  getVariantColorValue,
  getVariantImage,
  getVariantLabel,
  getVariantStock,
  summarizeStock,
} from "../studioEditModel";
import { Btn, Card, EmptyState, Icon, Menu, Notice, Pill, Select, Spinner, Stat, Thumb, cx } from "../StudioUi";

const LEVEL_META = {
  ok: { tone: "success", key: "skHealthOk" },
  low: { tone: "warning", key: "skHealthLow" },
  out: { tone: "danger", key: "skHealthOut" },
};

const normalize = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const StockTab = ({ studio }) => {
  const {
    tt,
    workspace,
    liveVariants,
    mediaDrafts,
    colorOption,
    productColors,
    locations,
    inventoryMode,
    recentMovements,
    isFetchingRecentMovements,
    focusVariantId,
    setFocusVariantId,
    openRestock,
    openAdjust,
    selectTab,
  } = studio;
  const isSerialized = inventoryMode === "serialized";
  const [query, setQuery] = useState("");
  const [colorFilter, setColorFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const selectedId = focusVariantId;
  // Per-location columns may be hidden on small screens only while the total
  // columns are visible; with a location filter they are the only numbers.
  const locationColumnClass = locationFilter ? "" : "pse-hide-md";

  const stock = summarizeStock(liveVariants, inventoryMode);
  const locationsWithStock = getLocationsWithStock(liveVariants);
  const allLocations = useMemo(() => collectStockLocations(liveVariants, locations), [liveVariants, locations]);
  const locationName = (location) =>
    location?.name || fill(tt("locationFallback"), { id: location?.id });

  // Columns: the filtered location, else every location that has stock rows.
  const columnLocations = useMemo(() => {
    if (isSerialized) return [];
    if (locationFilter) return allLocations.filter((location) => String(location.id) === locationFilter);
    const withRows = new Set(
      liveVariants.flatMap((variant) =>
        (variant.inventory?.aggregateByLocation || []).map((row) => Number(row.locationId))
      )
    );
    return allLocations.filter((location) => withRows.has(location.id));
  }, [allLocations, isSerialized, liveVariants, locationFilter]);

  const rows = useMemo(() => {
    const normalizedQuery = normalize(query);
    return liveVariants
      .map((variant) => {
        const total = getVariantStock(variant, inventoryMode);
        const scoped = locationFilter ? getLocationStock(variant, locationFilter) : total;
        return { variant, total, scoped };
      })
      .filter(({ variant, scoped }) => {
        if (normalizedQuery) {
          const haystack = normalize(
            [getVariantLabel(variant), variant.title, variant.sku, variant.barcode].filter(Boolean).join(" ")
          );
          if (!haystack.includes(normalizedQuery)) return false;
        }
        if (colorFilter && String(getVariantColorValue(variant, colorOption)?.optionValueId) !== colorFilter) {
          return false;
        }
        if (levelFilter === "attention" && scoped.level === "ok") return false;
        if (levelFilter && levelFilter !== "attention" && scoped.level !== levelFilter) return false;
        return true;
      });
  }, [colorFilter, colorOption, inventoryMode, levelFilter, liveVariants, locationFilter, query]);

  useEffect(() => {
    if (!selectedId && liveVariants.length) setFocusVariantId(liveVariants[0].id);
  }, [liveVariants, selectedId, setFocusVariantId]);

  const selected = liveVariants.find((variant) => variant.id === selectedId) || null;
  const selectedStock = selected ? getVariantStock(selected, inventoryMode) : null;
  const selectedImage = selected ? getVariantImage(selected, mediaDrafts, colorOption) : null;
  const selectedLevels = selected
    ? (selected.inventory?.aggregateByLocation || []).filter(
        (row) => Number(row.onHand || 0) > 0 || Number(row.reserved || 0) > 0
      )
    : [];

  if (!liveVariants.length) {
    return (
      <div className="pse-card pse-card--center">
        <EmptyState
          icon="cube"
          title={tt("skEmptyTitle")}
          action={
            <Btn variant="primary" onClick={() => selectTab("variants")}>
              {tt("tabVariants")}
            </Btn>
          }
        >
          {tt("skEmptyCopy")}
        </EmptyState>
      </div>
    );
  }

  const rowActions = (variant) => [
    { label: tt("skAddStock"), icon: "plus", onClick: () => openRestock(variant.id) },
    !isSerialized ? { label: tt("skAdjust"), icon: "sliders", onClick: () => openAdjust(variant.id) } : null,
  ];

  return (
    <div className="pse-layout">
      <div className="pse-layout__main">
        <Card
          title={tt("skTitle")}
          subtitle={tt("skCopy")}
          actions={
            <>
              <Btn
                variant="secondary"
                icon="sliders"
                onClick={() => openAdjust(selectedId)}
                disabled={isSerialized}
                title={isSerialized ? tt("skAdjustSerializedOff") : undefined}
              >
                {tt("skAdjust")}
              </Btn>
              <Btn variant="primary" icon="plus" onClick={() => openRestock()}>
                {tt("skAddStock")}
              </Btn>
            </>
          }
        >
          <div className="pse-stat-row">
            <Stat icon="cube" label={tt("skOnHand")} value={stock.onHand} hint={tt("skOnHandHint")} />
            <Stat icon="lock" tone="warning" label={tt("skReserved")} value={stock.reserved} hint={tt("skReservedHint")} />
            <Stat icon="checkCircle" tone="success" label={tt("skAvailable")} value={stock.available} hint={tt("skAvailableHint")} />
            <Stat
              icon="alert"
              tone={stock.out + stock.low ? "danger" : "neutral"}
              label={tt("skHealthNeedsAttention")}
              value={stock.out + stock.low}
              hint={fill(tt("skHealthNeedsAttentionCopy"), { out: stock.out, low: stock.low })}
            />
            {!isSerialized ? (
              <Stat
                icon="pin"
                label={tt("skLocations")}
                value={locationsWithStock.length}
                hint={locationsWithStock.map(locationName).join(", ") || "—"}
              />
            ) : null}
          </div>
          {isSerialized ? <Notice tone="info" title={tt("skSerializedTitle")}>{tt("skSerializedCopy")}</Notice> : null}
          {stock.onHand === 0 && !isSerialized ? <Notice tone="info">{tt("skNoStockYet")}</Notice> : null}
        </Card>

        <Card title={tt("skVariantsAndStock")}>
          <div className="pse-toolbar">
            <label className="pse-search">
              <Icon name="search" size={17} />
              <input
                type="search"
                value={query}
                placeholder={tt("skSearch")}
                aria-label={tt("skSearch")}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            {productColors.length ? (
              <Select aria-label={colorOption?.name} value={colorFilter} onChange={setColorFilter}>
                <option value="">{fill(tt("vrAllValues"), { name: colorOption?.name || "" })}</option>
                {productColors.map((color) => (
                  <option key={color.optionValueId} value={color.optionValueId}>
                    {color.value}
                  </option>
                ))}
              </Select>
            ) : null}
            {!isSerialized ? (
              <Select aria-label={tt("skAllLocations")} value={locationFilter} onChange={setLocationFilter}>
                <option value="">{tt("skAllLocations")}</option>
                {allLocations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {locationName(location)}
                  </option>
                ))}
              </Select>
            ) : null}
            <Select aria-label={tt("skAllHealth")} value={levelFilter} onChange={setLevelFilter}>
              <option value="">{tt("skAllHealth")}</option>
              <option value="attention">{tt("skHealthNeedsAttention")}</option>
              <option value="out">{tt("skHealthOut")}</option>
              <option value="low">{tt("skHealthLow")}</option>
              <option value="ok">{tt("skHealthOk")}</option>
            </Select>
          </div>

          {rows.length ? (
            <div className="pse-table-wrap">
              <table className="pse-table pse-table--stock">
                <thead>
                  {isSerialized ? (
                    <tr>
                      <th>{tt("vrColVariant")}</th>
                      <th className="pse-table__num">{tt("skAllUnits")}</th>
                      <th className="pse-table__num">{tt("skAvailable")}</th>
                      <th className="pse-table__num">{tt("skReserved")}</th>
                      <th className="pse-table__num pse-hide-md">{tt("skSold")}</th>
                      <th className="pse-table__num pse-hide-md">{tt("skReturned")}</th>
                      <th className="pse-table__num pse-hide-md">{tt("skDamaged")}</th>
                      <th>{tt("skHealth")}</th>
                      <th className="pse-table__actions">
                        <span className="sr-only">{tt("vrColActions")}</span>
                      </th>
                    </tr>
                  ) : (
                    <>
                      <tr className="pse-table__group-row">
                        <th rowSpan={2}>{tt("vrColVariant")}</th>
                        {columnLocations.map((location) => (
                          <th key={location.id} colSpan={3} className={`pse-table__group ${locationColumnClass}`}>
                            <Icon name="pin" size={13} /> {locationName(location)}
                          </th>
                        ))}
                        {!locationFilter ? (
                          <th colSpan={3} className="pse-table__group pse-table__group--total">
                            {tt("skTotal")}
                          </th>
                        ) : null}
                        <th rowSpan={2}>{tt("skHealth")}</th>
                        <th rowSpan={2} className="pse-table__actions">
                          <span className="sr-only">{tt("vrColActions")}</span>
                        </th>
                      </tr>
                      <tr className="pse-table__sub-row">
                        {columnLocations.map((location) => (
                          <React.Fragment key={location.id}>
                            <th className={`pse-table__num ${locationColumnClass}`}>{tt("skOnHand")}</th>
                            <th className={`pse-table__num ${locationColumnClass}`}>{tt("skShortReserved")}</th>
                            <th className={`pse-table__num ${locationColumnClass}`}>{tt("skShort")}</th>
                          </React.Fragment>
                        ))}
                        {!locationFilter ? (
                          <>
                            <th className="pse-table__num">{tt("skOnHand")}</th>
                            <th className="pse-table__num">{tt("skShortReserved")}</th>
                            <th className="pse-table__num">{tt("skShort")}</th>
                          </>
                        ) : null}
                      </tr>
                    </>
                  )}
                </thead>
                <tbody>
                  {rows.map(({ variant, total, scoped }) => {
                    const label = getVariantLabel(variant) || fill(tt("variantFallback"), { id: variant.id });
                    const image = getVariantImage(variant, mediaDrafts, colorOption);
                    const level = LEVEL_META[scoped.level];
                    const serialized = variant.inventory?.serialized || {};
                    const isSelected = variant.id === selectedId;
                    return (
                      <tr
                        key={variant.id}
                        id={`catalog-inventory-variant-${variant.id}`}
                        className={cx("pse-row", "pse-row--selectable", isSelected && "is-selected")}
                        onClick={(event) => {
                          if (event.target.closest("button, a, input, select")) return;
                          setFocusVariantId(variant.id);
                        }}
                      >
                        <td className="pse-cell-variant">
                          <Thumb src={image?.sourceUrl} size={40} alt="" />
                          <div className="pse-cell-variant__text">
                            <button
                              type="button"
                              className="pse-cell-variant__label pse-cell-variant__button"
                              aria-pressed={isSelected}
                              onClick={() => setFocusVariantId(variant.id)}
                            >
                              {label}
                            </button>
                            <span className="pse-cell-variant__meta">{variant.sku || "—"}</span>
                          </div>
                        </td>
                        {isSerialized ? (
                          <>
                            <td className="pse-table__num" data-label={tt("skAllUnits")}>{serialized.total || 0}</td>
                            <td className="pse-table__num is-available" data-label={tt("skAvailable")}>
                              {serialized.available || 0}
                            </td>
                            <td className="pse-table__num is-reserved" data-label={tt("skReserved")}>
                              {serialized.reserved || 0}
                            </td>
                            <td className="pse-table__num pse-hide-md" data-label={tt("skSold")}>
                              {serialized.sold || 0}
                            </td>
                            <td className="pse-table__num pse-hide-md" data-label={tt("skReturned")}>
                              {serialized.returned || 0}
                            </td>
                            <td className="pse-table__num pse-hide-md" data-label={tt("skDamaged")}>
                              {serialized.damaged || 0}
                            </td>
                          </>
                        ) : (
                          <>
                            {columnLocations.map((location) => {
                              const at = getLocationStock(variant, location.id);
                              return (
                                <React.Fragment key={location.id}>
                                  <td
                                    className={`pse-table__num ${locationColumnClass} pse-table__loc-start`}
                                    data-label={`${locationName(location)} · ${tt("skOnHand")}`}
                                  >
                                    {at.onHand}
                                  </td>
                                  <td
                                    className={`pse-table__num ${locationColumnClass} is-reserved`}
                                    data-label={`${locationName(location)} · ${tt("skReserved")}`}
                                  >
                                    {at.reserved}
                                  </td>
                                  <td
                                    className={`pse-table__num ${locationColumnClass} is-available`}
                                    data-label={`${locationName(location)} · ${tt("skAvailable")}`}
                                  >
                                    {at.available}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                            {!locationFilter ? (
                              <>
                                <td className="pse-table__num pse-table__loc-start" data-label={tt("skOnHand")}>
                                  <strong>{total.onHand}</strong>
                                </td>
                                <td className="pse-table__num is-reserved" data-label={tt("skReserved")}>
                                  {total.reserved}
                                </td>
                                <td className="pse-table__num is-available" data-label={tt("skAvailable")}>
                                  <strong>{total.available}</strong>
                                </td>
                              </>
                            ) : null}
                          </>
                        )}
                        <td data-label={tt("skHealth")}>
                          <Pill tone={level.tone}>{tt(level.key)}</Pill>
                        </td>
                        <td className="pse-table__actions">
                          <Menu label={tt("vrColActions")} items={rowActions(variant)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon="search" title={tt("vrNoMatch")} />
          )}
          <p className="pse-table-foot">{fill(tt("vrShown"), { shown: rows.length, total: liveVariants.length })}</p>
        </Card>
      </div>

      <aside className="pse-layout__side">
        <Card title={tt("skVariantDetails")}>
          {selected ? (
            <>
              <div className="pse-variant-card">
                <Thumb src={selectedImage?.sourceUrl} size={72} alt="" />
                <div>
                  <strong className="pse-variant-card__title">{getVariantLabel(selected)}</strong>
                  <span className="pse-variant-card__meta">{selected.sku || "—"}</span>
                  {selected.barcode ? <span className="pse-variant-card__meta">{selected.barcode}</span> : null}
                </div>
                <Pill tone={LEVEL_META[selectedStock.level].tone}>{tt(LEVEL_META[selectedStock.level].key)}</Pill>
              </div>
              <div className="pse-mini-stats pse-mini-stats--3">
                <div className="pse-mini-stat">
                  <strong>{selectedStock.onHand}</strong>
                  <span>{tt("skOnHand")}</span>
                </div>
                <div className="pse-mini-stat pse-mini-stat--warning">
                  <strong>{selectedStock.reserved}</strong>
                  <span>{tt("skReserved")}</span>
                </div>
                <div className="pse-mini-stat pse-mini-stat--success">
                  <strong>{selectedStock.available}</strong>
                  <span>{tt("skAvailable")}</span>
                </div>
              </div>
              {!isSerialized ? (
                <>
                  <h4 className="pse-subheading">{tt("skByLocation")}</h4>
                  {selectedLevels.length ? (
                    <ul className="pse-location-list">
                      {selectedLevels.map((row) => (
                        <li key={row.locationId}>
                          <span className="pse-location-list__name">
                            <Icon name="pin" size={16} />
                            {row.locationName || fill(tt("locationFallback"), { id: row.locationId })}
                          </span>
                          <span className="pse-location-list__nums">
                            <span>
                              <strong>{row.onHand}</strong>
                              <small>{tt("skOnHand")}</small>
                            </span>
                            <span className="is-reserved">
                              <strong>{row.reserved}</strong>
                              <small>{tt("skShortReserved")}</small>
                            </span>
                            <span className="is-available">
                              <strong>{row.available ?? Math.max(0, row.onHand - row.reserved)}</strong>
                              <small>{tt("skShort")}</small>
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="pse-muted pse-small">{tt("skNoLocationStock")}</p>
                  )}
                </>
              ) : null}
              <div className="pse-stack">
                {!isSerialized ? (
                  <Btn variant="primary" block icon="sliders" onClick={() => openAdjust(selected.id)}>
                    {tt("skAdjust")}
                  </Btn>
                ) : null}
                <Btn variant={isSerialized ? "primary" : "secondary"} block icon="plus" onClick={() => openRestock(selected.id)}>
                  {tt("skAddStock")}
                </Btn>
              </div>
            </>
          ) : (
            <p className="pse-muted">{tt("skVariantDetailsEmpty")}</p>
          )}
        </Card>

        <Card
          title={tt("skRecentMoves")}
          actions={
            <Btn variant="ghost" size="sm" iconRight="arrowRight" onClick={() => selectTab("history")}>
              {tt("skSeeAll")}
            </Btn>
          }
        >
          {isFetchingRecentMovements && !recentMovements.length ? (
            <Spinner />
          ) : recentMovements.length ? (
            <ul className="pse-move-list">
              {recentMovements.map((movement) => {
                const positive = Number(movement.quantityDelta) > 0;
                const variant = (workspace.variants || []).find((entry) => entry.id === movement.variantId);
                return (
                  <li key={movement.id} className="pse-move-list__item">
                    <span className={cx("pse-move-list__icon", positive ? "is-in" : "is-out")}>
                      <Icon name={positive ? "plus" : "minus"} size={15} strokeWidth={2.4} />
                    </span>
                    <span className="pse-move-list__text">
                      <strong>{tt(getMovementLabelKey(movement.movementType))}</strong>
                      <small>
                        {(variant ? getVariantLabel(variant) : movement.variantTitle) || "—"} ·{" "}
                        {movement.locationName || fill(tt("locationFallback"), { id: movement.locationId })}
                      </small>
                      <small>
                        {formatDateTime(movement.createdAt)} · {movement.actorName || tt("systemActor")}
                      </small>
                    </span>
                    <span className={cx("pse-move-list__delta", positive ? "is-in" : "is-out")}>
                      {positive ? "+" : ""}
                      {movement.quantityDelta}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="pse-muted pse-small">{tt("skNoMoves")}</p>
          )}
        </Card>
      </aside>
    </div>
  );
};

export default StockTab;
